import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { User, CreditCard, History, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { AlertSettings } from '../components/AlertComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [watchHistory, setWatchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const [userRes, historyRes] = await Promise.all([
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/user/continue-watching`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setUser(userRes.data);
      setWatchHistory(historyRes.data);
      localStorage.setItem('user', JSON.stringify(userRes.data));
    } catch (error) {
      console.error('Error fetching user data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  const isSubscriptionActive = user?.is_subscribed && 
    user?.subscription_expires && 
    new Date(user.subscription_expires) > new Date();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4" data-testid="dashboard-title">
              MY DASHBOARD
            </h1>
            <p className="text-muted-foreground text-lg">Welcome back, {user?.full_name}</p>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Profile Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-effect rounded-xl p-6"
              data-testid="profile-card"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Profile</h3>
                  <p className="text-sm text-muted-foreground">Account Info</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <p className="text-white">{user?.email}</p>
                </div>
                {user?.phone && (
                  <div>
                    <span className="text-muted-foreground">Phone:</span>
                    <p className="text-white">{user.phone}</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Subscription Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-effect rounded-xl p-6"
              data-testid="subscription-card"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isSubscriptionActive ? 'bg-accent' : 'bg-muted'
                }`}>
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Subscription</h3>
                  <p className="text-sm text-muted-foreground">
                    {isSubscriptionActive ? 'Active' : 'No active plan'}
                  </p>
                </div>
              </div>
              {isSubscriptionActive ? (
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Plan:</span>
                    <p className="text-white capitalize">{user.subscription_type}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Expires:</span>
                    <p className="text-white">
                      {new Date(user.subscription_expires).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ) : (
                <Button
                  className="w-full bg-primary text-black hover:bg-primary/90 font-bold mt-2"
                  onClick={() => navigate('/subscription')}
                  data-testid="subscribe-button"
                >
                  Subscribe Now
                </Button>
              )}
            </motion.div>

            {/* Watch History Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-effect rounded-xl p-6"
              data-testid="watch-history-card"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                  <History className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Watch History</h3>
                  <p className="text-sm text-muted-foreground">
                    {watchHistory.length} items
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Track your viewing activity and continue watching
              </p>
            </motion.div>
          </div>

          {/* Alert Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-12"
          >
            <AlertSettings />
          </motion.div>

          {/* Watch History Section */}
          {watchHistory.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              data-testid="history-section"
            >
              <h2 className="font-heading text-2xl text-white mb-6">CONTINUE WATCHING</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {watchHistory.slice(0, 6).map((item) => (
                  item.content && (
                    <div
                      key={item.id}
                      className="group relative overflow-hidden rounded-xl bg-card border border-border hover:border-primary/50 cursor-pointer glow-on-hover"
                      onClick={() => navigate(`/watch/${item.content.id}`)}
                      data-testid={`history-item-${item.id}`}
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={item.content.thumbnail_url}
                          alt={item.content.title}
                          className="w-full h-full object-cover"
                        />
                        {/* Progress bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                          <div 
                            className="h-full bg-primary"
                            style={{ width: `${item.progress}%` }}
                          />
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-white text-sm line-clamp-2">
                          {item.content.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {Math.round(item.progress)}% watched
                        </p>
                      </div>
                    </div>
                  )
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}